import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Box, Button, Flex, Input, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { apiClient } from '../../services/apiClient';
import { formatCountry } from '../../utils/country';
import DataTable from '../../components/table/DataTable';
import { FilterSidebar, type FilterSection } from '../../components/FilterSidebar';
import { FilterIcon } from '../../components/DataTable';

interface OrganizationSummary {
  id: string;
  name: string;
  email: string | null;
  status: string;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  memberCount: number;
  locationCount: number;
  createdAt: string;
  // 2026-04-17 refinement — admin list now surfaces the registration number so
  // support staff can confirm they're looking at the right organization when
  // customers call in with their legal identifier. Nullable because orgs that
  // haven't completed onboarding won't have it populated.
  companyName?: string | null;
  registrationNumber?: string | null;
  vatNumber?: string | null;
  country?: string | null;
}

interface OrganizationPage {
  items: OrganizationSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 20;

const columnHelper = createColumnHelper<OrganizationSummary>();

function getStatusColor(status: string) {
  switch (status) {
    case 'active':
    case 'trial':
      return 'green';
    case 'expired':
    case 'cancelled':
    case 'canceled':
      return 'red';
    case 'trialing':
      return 'blue';
    default:
      return 'gray';
  }
}

export function OrganizationsListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 0;
  const search = searchParams.get('search') ?? '';
  const statusFilter = searchParams.get('status') ?? '';
  const selectedStatuses = useMemo(() => statusFilter ? statusFilter.split(',') : [], [statusFilter]);

  const [searchInput, setSearchInput] = useState(search);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (searchInput) next.set('search', searchInput); else next.delete('search');
        next.delete('page');
        return next;
      }, { replace: true });
    }, 300);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [searchInput, setSearchParams]);

  const setPage = useCallback((p: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (p > 0) next.set('page', String(p)); else next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setStatusFilter = useCallback((value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set('status', value); else next.delete('status');
      next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const toggleStatus = useCallback((value: string) => {
    const current = statusFilter ? statusFilter.split(',') : [];
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setStatusFilter(next.join(','));
  }, [statusFilter, setStatusFilter]);

  const textColor = useColorModeValue('navy.700', 'white');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-organizations', page, search, statusFilter],
    queryFn: () => apiClient.get<OrganizationPage>('/admin/organizations', {
      page: page + 1,
      pageSize: PAGE_SIZE,
      ...(search && { search }),
      ...(statusFilter && { status: statusFilter }),
    }),
  });

  const columns = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (): any[] => [
      columnHelper.accessor('name', {
        header: () => t('admin.organizations.name'),
        cell: (info) => (
          <Text color={textColor} fontWeight="700" fontSize="sm">
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor((row) => row.subscriptionStatus || row.status, {
        id: 'status',
        header: () => t('admin.organizations.status'),
        cell: (info) => {
          const status = info.getValue();
          return (
            <Badge colorScheme={getStatusColor(status)} fontSize="xs" borderRadius="8px" px="10px">
              {status}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('country', {
        header: () => t('admin.organizations.country'),
        cell: (info) => formatCountry(info.getValue(), i18n.language) ?? '—',
      }),
      columnHelper.accessor('registrationNumber', {
        header: () => t('admin.organizations.registrationNumber'),
        cell: (info) => info.getValue() ?? '—',
      }),
      columnHelper.accessor('subscriptionPlan', {
        header: () => t('admin.organizations.plan'),
        cell: (info) => info.getValue() ?? '—',
      }),
      columnHelper.accessor('memberCount', {
        header: () => t('admin.organizations.members'),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('locationCount', {
        header: () => t('admin.organizations.locations'),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('createdAt', {
        header: () => t('admin.organizations.createdAt'),
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      }),
    ],
    [t, textColor, i18n.language],
  );

  const filterSections = useMemo<FilterSection[]>(() => [{
    key: 'status',
    label: t('admin.organizations.status'),
    options: [
      { value: 'trialing', label: t('admin.organizations.statuses.trialing') },
      { value: 'active', label: t('admin.organizations.statuses.active') },
      { value: 'expired', label: t('admin.organizations.statuses.expired') },
      { value: 'canceled', label: t('admin.organizations.statuses.cancelled') },
    ],
    selected: selectedStatuses,
    onToggle: toggleStatus,
  }], [t, selectedStatuses, toggleStatus]);

  const sidebarFilterCount = selectedStatuses.length;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="organizations-list-page">
      <Flex gap={4}>
        {isSidebarOpen && (
          <FilterSidebar
            sections={filterSections}
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            onClose={() => setIsSidebarOpen(false)}
            onClearAll={() => setStatusFilter('')}
          />
        )}

        <Flex direction="column" flex={1}>
          <DataTable
            headerContent={!isSidebarOpen ? (
              <Flex align="center" gap={3} mb={4}>
                <Input
                  placeholder={t('common.search')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  flex={1}
                  minW={200}
                  size="md"
                  data-testid="org-search-input"
                  aria-label={t('common.search')}
                />
                <Button
                  leftIcon={<FilterIcon />}
                  variant="outline"
                  size="md"
                  borderRadius="10px"
                  onClick={() => setIsSidebarOpen(true)}
                  data-testid="filter-toggle-button"
                >
                  {t('admin.library.filters.title', 'Filters')}
                  {sidebarFilterCount > 0 && (
                    <Badge ml={2} colorScheme="brand" borderRadius="full" fontSize="xs">
                      {sidebarFilterCount}
                    </Badge>
                  )}
                </Button>
              </Flex>
            ) : undefined}
            data={data?.items ?? []}
            columns={columns}
            totalCount={data?.totalCount}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            isLoading={isLoading}
            onRowClick={(row) => navigate(`/organizations/${row.id}`)}
            emptyText={t('admin.common.noResults')}
          />
        </Flex>
      </Flex>
    </Box>
  );
}
